import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import {
  adminCreateNewStructuredQuestion,
  approveStructuredEssayQuestion,
  deleteStructuredEssayQuestion,
  generateStructuredEssayQuestion,
  getStructuredEssayQuestionById,
  getStructuredEssayQuestionList,
  updateStructuredEssayQuestion,
} from './StructuredEssayAdminApi';
import { ISubTopic } from '../SubTopicSlice';
import { IBaseUser } from '../UserSlice';

export type TQuestionDifficulty = 'EASY' | 'MEDIUM' | 'HARD' | 'ANY';

export interface ISubQuestion {
  question: string;
  marks: number;
  markingScheme: string;
  sortingOrder: number;
}

export interface IStructuredEssayQuestion {
  _id?: string;
  mainQuestion: string;
  difficulty: TQuestionDifficulty;
  totalMarks: number;
  subQuestions: Array<ISubQuestion>;
  source?: string;
  tags?: string[];
  subTopics?: Array<{ subTopic: ISubTopic; paper: string }>;
  isApproved?: boolean;
  approvedBy?: IBaseUser;
}

export interface IstructuredEssayQuestionState {
  status: 'idle' | 'loading' | 'rejected';
  questions: Array<IStructuredEssayQuestion>;
  generatedQuestions: Array<IStructuredEssayQuestion>;
  threadId?: string;
  question?: IStructuredEssayQuestion;
}

const initialState: IstructuredEssayQuestionState = {
  status: 'idle',
  questions: [],
  generatedQuestions: [],
  threadId: undefined,
  question: undefined,
};

export const structuredEssayQuestionSlice = createSlice({
  name: 'structuredEssayQuestion',
  initialState,
  reducers: {
    reset: (state) => {
      state.status = 'idle';
      state.generatedQuestions = [];
      state.threadId = undefined;
    },
    resetQuestionList: (state) => {
      state.questions = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateStructuredEssayQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(generateStructuredEssayQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        state.generatedQuestions = action.payload.questions || [];
        if (action.payload.threadId) {
          state.threadId = action.payload.threadId;
        }
      })
      .addCase(generateStructuredEssayQuestion.rejected, (state) => {
        state.status = 'rejected';
        state.threadId = undefined;
      })
      // ADMIN CREATE NEW QUESTION
      .addCase(adminCreateNewStructuredQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminCreateNewStructuredQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        state.questions = [...state.questions, ...action.payload];
        state.generatedQuestions = [];
        state.threadId = undefined;
      })
      .addCase(adminCreateNewStructuredQuestion.rejected, (state) => {
        state.status = 'rejected';
      })
      // GET QUESTION LIST
      .addCase(getStructuredEssayQuestionList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getStructuredEssayQuestionList.fulfilled, (state, action) => {
        state.status = 'idle';
        state.questions = action.payload || [];
      })
      .addCase(getStructuredEssayQuestionList.rejected, (state) => {
        state.status = 'rejected';
      })
      // GET QUESTIONS BY ID
      .addCase(getStructuredEssayQuestionById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getStructuredEssayQuestionById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.question = action.payload;
      })
      .addCase(getStructuredEssayQuestionById.rejected, (state) => {
        state.status = 'rejected';
      })
      // APPROVE QUESTION
      .addCase(approveStructuredEssayQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(approveStructuredEssayQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        state.questions = state.questions.map((item) => {
          if (item._id === action.payload._id) {
            return {
              ...item,
              isApproved: action.payload.isApproved,
              approvedBy: action.payload.approvedBy,
            };
          }
          return item;
        });

        if (state.question && state.question._id === action.payload._id) {
          state.question = {
            ...state.question,
            isApproved: action.payload.isApproved,
            approvedBy: action.payload.approvedBy,
          };
        }
      })
      .addCase(approveStructuredEssayQuestion.rejected, (state) => {
        state.status = 'rejected';
      })
      // DELETE QUESTION
      .addCase(deleteStructuredEssayQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteStructuredEssayQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        const newList = state.questions.filter(
          (question) => question._id !== action.payload._id
        );
        state.questions = newList;
      })
      .addCase(deleteStructuredEssayQuestion.rejected, (state) => {
        state.status = 'rejected';
      })
      // UPDATE QUESTION
      .addCase(updateStructuredEssayQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateStructuredEssayQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        const newList = state.questions;

        const index = newList.findIndex(
          (item) => item._id === action.payload._id
        );

        if (index > -1) {
          newList[index] = action.payload;
        }

        state.questions = newList;

        if (
          state.question &&
          action.payload &&
          state.question._id === action.payload._id
        ) {
          state.question = {
            ...state.question,
            ...action.payload,
          };
        }
      })
      .addCase(updateStructuredEssayQuestion.rejected, (state) => {
        state.status = 'rejected';
      });
  },
});

export const getStructuredEssayQuestionState = (
  state: RootState
): RootState['structuredEssayQuestion'] => state.structuredEssayQuestion;

export const {
  reset,
  resetQuestionList,
} = structuredEssayQuestionSlice.actions;

export * from './StructuredEssayAdminApi';
export default structuredEssayQuestionSlice.reducer;
